import React, { useEffect, useState } from "react";
import { SelectList } from "components/table/DataTable/Fields";
import strings from "localisation/strings";
import { SelectItem } from "components/forms/Select";
import { Gallery } from "api/models/Gallery";
import styled from "styled-components";
import { formattedDate } from "utils";

interface GalleriesDropdownProps {
  allGalleries: Gallery[];
  values: string[];
  fieldName: string;
  width?: string;
  style?: string;
  onChange: (selectedField: any, selectedValue: any) => void;
  onSelect?: (selectedValues: Gallery[]) => void;
}

interface StringSelectOption {
  value: string;
  label: string;
}

const Container = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
`;

const getCity = (city: string) =>
  strings(
    `businessSettings.additional-settings.fields.galleries.cities.${city}`,
  );

const getUserGroup = (userGroup: string) =>
  strings(
    `businessSettings.additional-settings.fields.galleries.userGroups.${userGroup}`,
  );

const getStartDate = (startDate: string) => {
  if (new Date(startDate) > new Date()) {
    return (
      strings(
        `businessSettings.additional-settings.fields.galleries.startDate.from`,
      ) + formattedDate(startDate, "MM.DD.YYYY")
    );
  }
  return "";
};

const mapToSelectOption = (gallery: Gallery): StringSelectOption => ({
  value: gallery.id,
  label: `${gallery.title["de"]} (${getUserGroup(
    gallery.userGroup,
  )}) ${getStartDate(gallery.startDate)}`,
});

const GalleriesDropdown = ({
  allGalleries,
  values,
  fieldName,
  width,
  style,
  onChange,
  onSelect,
}: GalleriesDropdownProps) => {
  const [selectedGalleries, setSelectedGalleries] = useState<Gallery[]>([]);
  const [
    selectedGalleryValuesForSelectList,
    setSelectedGalleryValuesForSelectList,
  ] = useState<any>([]);

  useEffect(() => {
    const galleries = allGalleries.filter(
      gallery => gallery.id === values.find(value => value === gallery.id),
    );
    setSelectedGalleries(galleries);
    setSelectedGalleryValuesForSelectList(galleries.map(mapToSelectOption));
    onSelect?.(galleries);
  }, []);

  useEffect(() => {
    setSelectedGalleryValuesForSelectList(
      selectedGalleries.map(mapToSelectOption),
    );
    onChange(
      fieldName,
      selectedGalleries.map(gallery => gallery.id),
    );
  }, [selectedGalleries]);

  const onItemSelect = (selectedItems: SelectItem[]) => {
    const galleries = allGalleries.filter(
      gallery =>
        gallery.id ===
        selectedItems.find(selected => selected.value === gallery.id)?.value,
    );
    setSelectedGalleries(galleries);
    onSelect?.(galleries);
  };

  const galleriesForSelectList: StringSelectOption[] = allGalleries
    .map(mapToSelectOption)
    .sort((a, b) => a.label.localeCompare(b.label));

  return (
    <Container>
      <SelectList
        options={galleriesForSelectList}
        onChange={onItemSelect}
        placeholder={strings(
          "businessSettings.additional-settings.fields.galleries.placeholder",
        )}
        width={width ?? "100%"}
        classNamePrefix="galleriesDropdown"
        value={selectedGalleryValuesForSelectList}
        style={style}
        isMulti
      />
    </Container>
  );
};

export default GalleriesDropdown;
